/**
 * Platform-admin schemas (Phase 8).
 *
 * Separate identity from per-tenant users. See docs/PRD-PLATFORM-ADMIN.md.
 * These schemas back the /api/v1/platform/* surface.
 */

import { z } from 'zod';

import { LOCALES } from '../enums.js';

import { TenantBillingStatusSchema, TenantPlanSchema, TenantSchema } from './tenant.js';

// -----------------------------------------------------------------------------
// Platform admin (identity)
// -----------------------------------------------------------------------------

export const PlatformAdminStatusSchema = z.enum(['active', 'disabled']);
export type PlatformAdminStatus = z.infer<typeof PlatformAdminStatusSchema>;

export const PlatformAdminSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().toLowerCase(),
  fullName: z.string().min(1).max(200),
  status: PlatformAdminStatusSchema,
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PlatformAdmin = z.infer<typeof PlatformAdminSchema>;

export const PlatformAdminCreateSchema = z.object({
  email: z.string().email().toLowerCase(),
  fullName: z.string().min(1).max(200),
  password: z.string().min(12).max(200).optional(),
});
export type PlatformAdminCreate = z.infer<typeof PlatformAdminCreateSchema>;

export const PlatformAdminPatchSchema = z.object({
  fullName: z.string().min(1).max(200).optional(),
  status: PlatformAdminStatusSchema.optional(),
});
export type PlatformAdminPatch = z.infer<typeof PlatformAdminPatchSchema>;

// -----------------------------------------------------------------------------
// Platform auth
// -----------------------------------------------------------------------------

export const PlatformLoginSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(1),
});
export type PlatformLogin = z.infer<typeof PlatformLoginSchema>;

// -----------------------------------------------------------------------------
// Tenant provisioning (POST /platform/tenants)
// -----------------------------------------------------------------------------

const slug = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'slug must be lowercase kebab-case');

const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);

// Lowercased ASCII FQDN: ≥2 dot-separated labels, each 1-63 chars of [a-z0-9-]
// with no leading/trailing hyphen, ≤253 total. Punycode (xn--) passes because
// each label is plain [a-z0-9-]; Unicode IDNs must be converted to ASCII first.
const hostname = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .regex(
    /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/,
    'must be a valid fully-qualified hostname',
  );

export const TenantProvisioningSchema = z.object({
  name: z.string().min(1).max(200),
  slug,
  defaultLocale: z.enum(LOCALES).optional(),       // defaults to 'en'
  baseCurrencyCode: currencyCode.optional(),       // defaults to 'USD'
  plan: TenantPlanSchema.optional(),               // defaults to 'free'
  ownerEmail: z.string().email().toLowerCase(),
  ownerFullName: z.string().min(1).max(200),
  // Optional billing intent at creation time. If omitted the
  // provisioning service sets a 14-day trial; if `yearlyPriceAmount`
  // is set it stamps the price columns and starts immediately as
  // 'active' with a 1-year period instead of 'trialing'.
  yearlyPriceAmount: z.string().regex(/^-?\d+(\.\d{1,4})?$/).optional(),
  yearlyPriceCurrencyCode: currencyCode.optional(),
  logoUrl: z.string().url().optional(),
  // Optional: attach a `pending` custom domain at creation. It is NOT verified
  // until the operator runs the verify endpoint (Phase 2 of the build).
  initialDomain: hostname.optional(),
  initialDomainKind: z.enum(['app', 'storefront']).optional(),
  // Optional per-tenant module on/off map (settings.modules). Omitted keys ⇒
  // enabled (fail-open). Lets the platform admin pick the tenant's modules at
  // creation; editable afterwards via PUT /platform/tenants/:id/modules.
  modules: z.record(z.boolean()).optional(),
});
export type TenantProvisioning = z.infer<typeof TenantProvisioningSchema>;

/** Body for PUT /platform/tenants/:id/modules — the full on/off flag map. */
export const TenantModulesSchema = z.object({
  modules: z.record(z.boolean()),
});
export type TenantModules = z.infer<typeof TenantModulesSchema>;

export const TenantProvisionedSchema = z.object({
  tenant: TenantSchema,
  ownerUser: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    fullName: z.string(),
  }),
  // Returned ONCE; the platform admin delivers it out-of-band. The
  // server never stores the plaintext.
  tempPassword: z.string(),
});
export type TenantProvisioned = z.infer<typeof TenantProvisionedSchema>;

// -----------------------------------------------------------------------------
// Tenant edits + lifecycle (PATCH / suspend / reactivate)
// -----------------------------------------------------------------------------

const moneyAmount = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'expected decimal amount');
const isoDate = z.string().datetime();

export const PlatformTenantPatchSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  plan: TenantPlanSchema.optional(),
  defaultLocale: z.enum(LOCALES).optional(),
  baseCurrencyCode: currencyCode.optional(),
  // Logo + billing — every field is patchable independently. Null
  // clears the field (e.g. removing a logo or clearing a trial end).
  logoUrl: z.string().url().nullable().optional(),
  billingStatus: TenantBillingStatusSchema.optional(),
  currentPeriodStart: isoDate.nullable().optional(),
  currentPeriodEnd: isoDate.nullable().optional(),
  trialEndsAt: isoDate.nullable().optional(),
  yearlyPriceAmount: moneyAmount.nullable().optional(),
  yearlyPriceCurrencyCode: currencyCode.nullable().optional(),
  billingNotes: z.string().max(2_000).nullable().optional(),
});
export type PlatformTenantPatch = z.infer<typeof PlatformTenantPatchSchema>;

/**
 * Platform-admin reset of a tenant's OWNER login password. `password` optional:
 * provide one to set it, or omit to have the server generate a temporary one.
 * The chosen/generated password is returned to the admin exactly once.
 */
export const ResetTenantPasswordSchema = z.object({
  password: z
    .string()
    .min(10)
    .max(256)
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[0-9]/, 'Password must contain a digit')
    .optional(),
});
export type ResetTenantPassword = z.infer<typeof ResetTenantPasswordSchema>;

export const ResetTenantPasswordResultSchema = z.object({
  email: z.string().email(),
  tempPassword: z.string(),
});
export type ResetTenantPasswordResult = z.infer<typeof ResetTenantPasswordResultSchema>;

/**
 * Record-payment is its own endpoint (POST .../record-payment) rather
 * than a PATCH because it has side effects: it bumps last_payment_at
 * to now (server side), flips billing_status to 'active' if it was
 * past_due/unpaid, and extends current_period_end by the requested
 * number of years.
 */
export const RecordPaymentSchema = z.object({
  // Number of yearly periods the payment covers (default 1).
  years: z.number().int().min(1).max(10).default(1),
  // Optional notes — wire ref, cheque #, "paid in cash 5/20", etc.
  // Goes into billing_notes prepended with a date stamp.
  note: z.string().max(500).optional(),
});
export type RecordPayment = z.infer<typeof RecordPaymentSchema>;

export const TenantSuspendSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type TenantSuspend = z.infer<typeof TenantSuspendSchema>;

// -----------------------------------------------------------------------------
// Impersonation (POST /platform/tenants/:id/impersonate)
// -----------------------------------------------------------------------------

export const ImpersonateSchema = z.object({
  // Reason is required for the dual-audit trail — both the tenant
  // and platform_audit logs record why the impersonation happened.
  reason: z.string().min(1).max(500),
});
export type Impersonate = z.infer<typeof ImpersonateSchema>;

// -----------------------------------------------------------------------------
// Platform audit log entry
// -----------------------------------------------------------------------------

export const PlatformAuditEntrySchema = z.object({
  id: z.string().uuid(),
  platformAdminId: z.string().uuid(),
  action: z.string(),
  targetTenantId: z.string().uuid().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  diff: z.unknown().nullable(),
  createdAt: z.string().datetime(),
});
export type PlatformAuditEntry = z.infer<typeof PlatformAuditEntrySchema>;

// -----------------------------------------------------------------------------
// Custom domains (POST /platform/tenants/:id/domains)
// -----------------------------------------------------------------------------

export const DomainKindSchema = z.enum(['app', 'storefront']);
export type DomainKind = z.infer<typeof DomainKindSchema>;

export const DomainStatusSchema = z.enum(['pending', 'verified', 'disabled']);
export type DomainStatus = z.infer<typeof DomainStatusSchema>;

/** Operator input — the server controls status/token/verifiedAt/isPrimary. */
export const TenantDomainAddSchema = z.object({
  hostname,
  kind: DomainKindSchema.optional(), // defaults to 'app' in the service
});
export type TenantDomainAdd = z.infer<typeof TenantDomainAddSchema>;

/** Serialized tenant_domains row. `verificationToken` is surfaced so the
 *  dashboard can show the exact `_erp-verify.<host>` TXT record to publish. */
export const TenantDomainSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  hostname: z.string(),
  kind: DomainKindSchema,
  status: DomainStatusSchema,
  verificationToken: z.string(),
  verifiedAt: z.string().datetime().nullable(),
  isPrimary: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TenantDomain = z.infer<typeof TenantDomainSchema>;
